import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, TextField,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';
import { useAuth } from '../../hook';
import {
  createExpense, getExpenseCategories, getExpenses, getRecurringExpenseStatus, payRecurringExpense,
} from './clientApi';
import './client.scss';

const today = () => new Date().toISOString().slice(0, 10);
const firstDay = (value) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-01`;
const lastDay = (value) => {
  const date = new Date(value.getFullYear(), value.getMonth() + 1, 0);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const defaultDateForMonth = (value) => {
  const currentDay = new Date().getDate();
  const maximumDay = new Date(value.getFullYear(), value.getMonth() + 1, 0).getDate();
  const day = Math.min(currentDay, maximumDay);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
const monthName = (value) => new Intl.DateTimeFormat('es-PE', {
  month: 'long', year: 'numeric',
}).format(value);
const money = (value, currency = 'PEN') => `${currency === 'USD' ? 'USD' : 'S/'} ${Number(value || 0).toFixed(2)}`;
const readableDate = (value) => {
  if (!value) return 'Sin fecha';
  const [year, monthValue, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('es-PE', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(year, monthValue - 1, day));
};

export function NewExpense() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recurringSectionRef = useRef(null);
  const { auth } = useAuth();
  const [month, setMonth] = useState(() => new Date());
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [payment, setPayment] = useState({ amount: '', paid_at: today() });
  const [expenses, setExpenses] = useState([]);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [form, setForm] = useState({ date: today(), amount: '', category_id: '', notes: '' });
  const period = useMemo(() => firstDay(month), [month]);
  const visibleRows = useMemo(() => rows.filter(({ recurring }) => (
    recurring.frequency === 'monthly'
    || (
      recurring.frequency === 'yearly'
      && Number(recurring.yearly_month) === month.getMonth() + 1
    )
  )), [rows, month]);

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await getRecurringExpenseStatus(auth.token, period));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchParams.get('seccion') === 'recurrentes') {
      window.requestAnimationFrame(() => recurringSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [searchParams]);
  useEffect(() => {
    getExpenseCategories(auth.token).then(setCategories).catch((err) => setError(err.message));
  }, [auth.token]);

  const moveMonth = (amount) => {
    setExpensesOpen(false);
    setMonth((current) => new Date(current.getFullYear(), current.getMonth() + amount, 1));
  };

  const openPayment = (row) => {
    setSelected(row);
    setPayment({
      amount: row.recurring.default_amount || '',
      paid_at: defaultDateForMonth(month),
    });
  };

  const loadExpenses = async () => {
    if (expensesOpen) {
      setExpensesOpen(false);
      return;
    }
    setExpensesLoading(true);
    setError('');
    try {
      setExpenses(await getExpenses(auth.token, period, lastDay(month)));
      setExpensesOpen(true);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los gastos del mes.');
    } finally {
      setExpensesLoading(false);
    }
  };

  const submitExpense = async () => {
    if (!form.date || !form.amount || Number(form.amount) <= 0 || !form.category_id) {
      setError('Completa la fecha, un monto mayor que cero y la categoría.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createExpense(auth.token, {
        date: form.date, amount: form.amount, category_id: Number(form.category_id),
        notes: form.notes.trim(), currency: 'PEN', method: 'transfer',
      });
      setDialogOpen(false);
      setForm({ date: today(), amount: '', category_id: '', notes: '' });
      if (expensesOpen) {
        setExpenses(await getExpenses(auth.token, period, lastDay(month)));
      }
      toast.success('Gasto registrado correctamente.');
    } catch (err) {
      setError(err.message || 'No se pudo registrar el gasto.');
    } finally {
      setSaving(false);
    }
  };

  const confirmPayment = async () => {
    if (!selected || !payment.paid_at || !payment.amount || Number(payment.amount) <= 0) {
      setError('Ingresa una fecha y un monto mayor que cero.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await payRecurringExpense(auth.token, selected.recurring.id, {
        amount: payment.amount, period, paid_at: payment.paid_at,
      });
      setSelected(null);
      toast.success('Pago recurrente confirmado.');
      await loadRows();
      if (expensesOpen) {
        setExpenses(await getExpenses(auth.token, period, lastDay(month)));
      }
    } catch (err) {
      setError(err.message || 'No se pudo confirmar el pago.');
      setSelected(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="client-page client-expense-page">
      <header className="client-header">
        <IconButton onClick={() => navigate('/')}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
        <h1>Nuevo gasto</h1><span />
      </header>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <div className="client-expense-actions">
        <Button onClick={() => setDialogOpen(true)}>Agregar un nuevo gasto</Button>
      </div>
      <hr className="client-expense-divider" />
      <section ref={recurringSectionRef}>
        <span>Gastos recurrentes</span>
        <div className="client-month-picker">
          <IconButton onClick={() => moveMonth(-1)} aria-label="Mes anterior"><ChevronLeftIcon /></IconButton>
          <strong>{monthName(month)}</strong>
          <IconButton onClick={() => moveMonth(1)} aria-label="Mes siguiente"><ChevronRightIcon /></IconButton>
        </div>
        <p className="client-month-help">Por defecto se muestra el mes actual</p>
        {loading ? <div className="client-empty"><CircularProgress size={28} /></div> : (
          <div className="client-recurring-list">
            {!visibleRows.length && <div className="client-empty">No hay gastos recurrentes para este mes.</div>}
            {visibleRows.map((row) => {
              const paid = row.occurrence.status === 'paid';
              return (
                <article className={`client-recurring-card${paid ? ' client-recurring-card--paid' : ''}`} key={row.recurring.id}>
                  <div className="client-recurring-card__content">
                    <span className="client-recurring-card__eyebrow">
                      {row.recurring.frequency === 'yearly' ? 'Anual' : 'Mensual'}
                    </span>
                    <div className="client-recurring-card__name">{row.recurring.name}</div>
                    <div className="client-recurring-card__meta">
                      {paid
                        ? `Pagado el ${readableDate(row.occurrence.paid_at)}`
                        : `Vence el ${readableDate(row.occurrence.due_date)}`}
                    </div>
                  </div>
                  <div className="client-recurring-card__aside">
                    <strong className="client-recurring-card__amount">
                      {paid
                        ? money(row.occurrence.amount_paid, row.recurring.currency)
                        : (row.recurring.default_amount
                          ? money(row.recurring.default_amount, row.recurring.currency)
                          : 'Sin monto')}
                    </strong>
                    {paid ? (
                      <span className="client-recurring-card__status"><CheckIcon fontSize="small" /> Pagado</span>
                    ) : (
                      <Button size="small" variant="contained" onClick={() => openPayment(row)}>Registrar</Button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      <Dialog className="client-expense-dialog" open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Agregar un nuevo gasto</DialogTitle>
        <DialogContent className="client-expense-dialog__form">
          <TextField label="Fecha" type="date" value={form.date} InputLabelProps={{ shrink: true }} onChange={(event) => setForm({ ...form, date: event.target.value })} />
          <TextField label="Monto (PEN)" type="number" value={form.amount} inputProps={{ min: '0.01', step: '0.01' }} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
          <Autocomplete options={categories} getOptionLabel={(option) => option.name || ''}
            value={categories.find((category) => category.id === Number(form.category_id)) || null}
            onChange={(_, value) => setForm({ ...form, category_id: value?.id || '' })}
            renderInput={(params) => <TextField {...params} label="Categoría" placeholder="Buscar categoría" />} />
          <TextField label="Concepto" value={form.notes} multiline minRows={2} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" disabled={saving} onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={submitExpense}>{saving ? 'Guardando...' : 'Enviar gasto'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog className="client-expense-dialog" open={Boolean(selected)} onClose={saving ? undefined : () => setSelected(null)} fullWidth maxWidth="xs">
        <DialogTitle>Registrar pago recurrente</DialogTitle>
        <DialogContent className="client-expense-dialog__form">
          <p>{selected?.recurring.name} · {monthName(month)}</p>
          <TextField label={`Monto (${selected?.recurring.currency || 'PEN'})`} type="number"
            value={payment.amount} inputProps={{ min: '0.01', step: '0.01' }}
            onChange={(event) => setPayment({ ...payment, amount: event.target.value })} />
          <TextField label="Fecha real de pago" type="date" value={payment.paid_at}
            InputLabelProps={{ shrink: true }} onChange={(event) => setPayment({ ...payment, paid_at: event.target.value })} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" disabled={saving} onClick={() => setSelected(null)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={confirmPayment}>{saving ? 'Guardando...' : 'Confirmar pago'}</Button>
        </DialogActions>
      </Dialog>

      <div className="client-month-expenses-action">
        <Button variant="text" disabled={expensesLoading} onClick={loadExpenses}>
          {expensesLoading ? 'Cargando...' : `${expensesOpen ? 'Ocultar' : 'Ver todos los'} gastos de ${monthName(month)}`}
        </Button>
      </div>
      {expensesOpen && (
        <div className="client-expenses-table-wrap">
          <table className="client-expenses-table">
            <thead><tr><th>Fecha</th><th>Concepto</th><th>Monto</th></tr></thead>
            <tbody>
              {!expenses.length && <tr><td colSpan="3">No hay gastos registrados.</td></tr>}
              {expenses.map((expense) => (
                <tr key={expense.id}>
                  <td>{readableDate(expense.date)}</td>
                  <td>{expense.notes || expense.category_name || 'Sin concepto'}</td>
                  <td>{money(expense.amount, expense.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
